import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import {
  Eye,
  Users,
  UserCheck,
  Clock,
  TrendingDown,
  Download,
  RefreshCw,
  Mail,
  Gem,
  ExternalLink,
  Send,
  Loader2,
  Table as TableIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import {
  isoDate,
  formatDuration,
  presetRanges,
  SOURCE_COLORS,
  DRAW_BAR_COLORS,
} from "./analytics/analyticsUtils";
import { useAnalytics } from "./analytics/useAnalytics";
import { TrendBadge, MetricCard, RankedBarChart } from "./analytics/AnalyticsCharts";
import { AdInsightsTable } from "./analytics/AdInsightsTable";

const withTerminal = (rows) =>
  rows?.map((r) => (r.terminal ? { ...r, name: `${r.name} · ${r.terminal}` } : r));


const TICK_STYLE = { fontSize: 11 };
const LEGEND_STYLE = { fontSize: 12 };
const pieTooltipFmt = (v, name, p) => [`${v} (${p.payload.pct}%)`, name];

export function AnalyticsPanel() {
  const presets = useMemo(() => presetRanges(), []);
  // Default: Last 7 days
  const [pendingStart, setPendingStart] = useState(presets[1].start);
  const [pendingEnd, setPendingEnd] = useState(presets[1].end);
  const [appliedStart, setAppliedStart] = useState(presets[1].start);
  const [appliedEnd, setAppliedEnd] = useState(presets[1].end);
  const [activePreset, setActivePreset] = useState("last_7");

  const {
    data,
    loading,
    error,
    fetchSummary,
    exportReport,
    downloadSubsXlsx,
    subsOpen,
    setSubsOpen,
    subsList,
    subsLoading,
    openSubsList,
    emailTo,
    setEmailTo,
    emailSending,
    emailStatus,
    sendReport,
    pieData,
    lastUpdatedStr,
    maxDrawClicks,
  } = useAnalytics();

  // Same files the email attaches: analytics PDF + subscribers XLSX for the applied range.
  const downloadReportFiles = async () => {
    await exportReport("pdf", appliedStart, appliedEnd);
    await downloadSubsXlsx(appliedStart, appliedEnd);
  };

  useEffect(() => {
    fetchSummary(appliedStart, appliedEnd);
  }, [appliedStart, appliedEnd, fetchSummary]);

  const applyPreset = (p) => {
    setPendingStart(p.start);
    setPendingEnd(p.end);
    setActivePreset(p.id);
    setAppliedStart(p.start);
    setAppliedEnd(p.end);
  };

  const applyManual = () => {
    setActivePreset(null);
    setAppliedStart(pendingStart);
    setAppliedEnd(pendingEnd);
  };

  const resetRange = () => applyPreset(presets[1]); // Last 7 Days default

  return (
    <div className="ddf-an-panel" data-testid="admin-analytics-panel">
      {/* Calendar date range picker */}
      <div className="ddf-an-range-card">
        <div className="ddf-an-range-row">
          <div className="ddf-an-range-field">
            <Label className="ddf-an-range-label">Start date</Label>
            <DatePicker
              value={isoDate(pendingStart)}
              onChange={(iso) => {
                if (!iso) return;
                setPendingStart(new Date(iso));
                setActivePreset(null);
              }}
              testId="an-start-date"
            />
          </div>
          <div className="ddf-an-range-field">
            <Label className="ddf-an-range-label">End date</Label>
            <DatePicker
              value={isoDate(pendingEnd)}
              onChange={(iso) => {
                if (!iso) return;
                setPendingEnd(new Date(iso));
                setActivePreset(null);
              }}
              testId="an-end-date"
            />
          </div>
          <div className="ddf-an-range-actions">
            <Button size="sm" onClick={applyManual} data-testid="an-apply">Apply</Button>
            <Button size="sm" variant="outline" onClick={resetRange} data-testid="an-reset">Reset</Button>
          </div>
        </div>
        <div className="ddf-an-presets" role="group" aria-label="Quick range presets">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`ddf-an-preset ${activePreset === p.id ? "is-active" : ""}`}
              onClick={() => applyPreset(p)}
              data-testid={`an-preset-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="ddf-an-range-banner" data-testid="an-range-banner">
          Showing data from: <strong>{data?.currentRange?.label || `${isoDate(appliedStart)} – ${isoDate(appliedEnd)}`}</strong>
        </div>
      </div>

      <div className="ddf-an-toolbar">
        <div className="ddf-an-meta">
          <span data-testid="an-last-updated">
            Last updated: <strong>{lastUpdatedStr}</strong> (Asia/Dubai)
          </span>
        </div>
        <div className="ddf-an-toolbar-right">
          <button
            type="button"
            className="ddf-an-refresh"
            onClick={() => fetchSummary(appliedStart, appliedEnd)}
            data-testid="an-refresh"
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <Button size="sm" variant="outline" onClick={() => exportReport("csv", appliedStart, appliedEnd)} data-testid="an-export-csv">
            <Download size={14} /> CSV
          </Button>
          <Button size="sm" onClick={() => exportReport("pdf", appliedStart, appliedEnd)} data-testid="an-export-pdf">
            <Download size={14} /> PDF
          </Button>
        </div>
      </div>

      {error && <div className="ddf-an-error" data-testid="an-error">{error}</div>}
      {loading && !data && <div className="ddf-an-loading" data-testid="an-loading">Loading analytics…</div>}

      {data && (
        <>
          {/* Page Traffic metric cards */}
          <div className="ddf-an-grid" data-testid="an-metric-grid">
            <MetricCard icon={Eye} label="Page Views" value={data.pageViews} trend={data.trends?.pageViews} testid="metric-page-views" lastUpdated={lastUpdatedStr} />
            <MetricCard icon={Users} label="Unique Visitors" value={data.uniqueVisitors} trend={data.trends?.uniqueVisitors} testid="metric-unique-visitors" lastUpdated={lastUpdatedStr} />
            <MetricCard icon={UserCheck} label="New / Returning" value={`${data.newPct}% / ${data.returningPct}%`} trend={data.trends?.returningPct} testid="metric-new-returning" lastUpdated={lastUpdatedStr} />
            <MetricCard icon={Clock} label="Avg Session" value={formatDuration(data.avgSessionDuration)} trend={data.trends?.avgSessionDuration} testid="metric-avg-session" lastUpdated={lastUpdatedStr} />
            <MetricCard icon={TrendingDown} label="Bounce Rate" value={`${data.bounceRate}%`} trend={data.trends?.bounceRate} invertGood testid="metric-bounce-rate" lastUpdated={lastUpdatedStr} />
          </div>

          {/* Subscriber + Register row */}
          <div className="ddf-an-grid ddf-an-grid--3" data-testid="an-engage-grid">
            <div className="ddf-an-metric" data-testid="metric-subscriptions">
              <div className="ddf-an-metric-head">
                <Mail size={16} className="ddf-an-metric-icon" />
                <span className="ddf-an-metric-label">Email Subscriptions</span>
              </div>
              <div className="ddf-an-metric-value">{data.subscriptions.rangeNew}</div>
              <div className="ddf-an-metric-sub">
                <span>Total: {data.subscriptions.total}</span>
                <span>Rate: {data.subscriptions.rate}%</span>
              </div>
              <TrendBadge trend={data.trends?.subscriptions} />
              <div className="ddf-an-metric-actions">
                <button type="button" onClick={() => openSubsList(appliedStart, appliedEnd)} className="ddf-an-link-btn" data-testid="an-view-subs">
                  <TableIcon size={12} /> View Subscriber List
                </button>
                <button type="button" onClick={() => downloadSubsXlsx(appliedStart, appliedEnd)} className="ddf-an-link-btn" data-testid="an-download-subs-xlsx">
                  <Download size={12} /> Excel (.xlsx)
                </button>
              </div>
            </div>

            <div className="ddf-an-metric" data-testid="metric-register-clicks">
              <div className="ddf-an-metric-head">
                <ExternalLink size={16} className="ddf-an-metric-icon" />
                <span className="ddf-an-metric-label">Register With Us</span>
              </div>
              <div className="ddf-an-metric-value">{data.registerClicks.rangeClicks}</div>
              <div className="ddf-an-metric-sub">
                <span>Total: {data.registerClicks.total}</span>
                <span>CTR: {data.registerClicks.ctr}%</span>
              </div>
              <TrendBadge trend={data.trends?.registerClicks} />
            </div>

            <div className="ddf-an-metric" data-testid="metric-draw-section-views">
              <div className="ddf-an-metric-head">
                <Eye size={16} className="ddf-an-metric-icon" />
                <span className="ddf-an-metric-label">Exclusive Draw Section Views</span>
              </div>
              <div className="ddf-an-metric-value">{data.drawSectionViews}</div>
              <div className="ddf-an-metric-sub">
                <span>Popup Opens: {data.popupOpens}</span>
              </div>
            </div>

            <div className="ddf-an-metric" data-testid="metric-banner-ad-clicks">
              <div className="ddf-an-metric-head">
                <Eye size={16} className="ddf-an-metric-icon" />
                <span className="ddf-an-metric-label">Banner Ad Clicks</span>
              </div>
              <div className="ddf-an-metric-value">{data.bannerAdClicks?.total ?? 0}</div>
              <div className="ddf-an-metric-sub">
                {(data.bannerAdClicks?.items || []).slice(0, 3).map((a) => (
                  <span key={a.label}>{a.label}: {a.count}</span>
                ))}
                {(data.bannerAdClicks?.items || []).length === 0 && <span>No ad clicks yet</span>}
              </div>
            </div>
          </div>

          {/* Three Draw button click cards with comparative bars */}
          <section className="ddf-an-chart-card" data-testid="an-draw-buttons">
            <h4 className="ddf-an-chart-title">
              <Gem size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              Exclusive Draw Button Clicks
            </h4>
            <div className="ddf-an-draw-cards">
              {data.drawButtonClicks.map((d) => {
                const widthPct = Math.round((d.rangeClicks / maxDrawClicks) * 100);
                return (
                  <div key={d.key} className="ddf-an-draw-card" data-testid={`metric-draw-${d.key}`}>
                    <div className="ddf-an-draw-card-label">{d.label}</div>
                    <div className="ddf-an-draw-card-row">
                      <div className="ddf-an-draw-card-num">{d.rangeClicks}</div>
                      <div className="ddf-an-draw-card-bar-track">
                        <div
                          className="ddf-an-draw-card-bar-fill"
                          style={{ width: `${widthPct}%`, background: DRAW_BAR_COLORS[d.key] || "#0ea5e9" }}
                        />
                      </div>
                    </div>
                    <div className="ddf-an-draw-card-sub">
                      <span>All-time: {d.totalClicks}</span>
                      <span>CTR: {d.ctr}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <AdInsightsTable rows={data.adInsights || []} />

          {/* Line chart */}
          <section className="ddf-an-chart-card" data-testid="an-line-chart">
            <h4 className="ddf-an-chart-title">Page Views & Unique Visitors</h4>
            <div className="ddf-an-chart-body">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={TICK_STYLE} />
                  <YAxis tick={TICK_STYLE} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Line type="monotone" dataKey="pageViews" name="Page Views" stroke="#0ea5e9" strokeWidth={2} />
                  <Line type="monotone" dataKey="uniqueVisitors" name="Unique Visitors" stroke="#e11d48" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Promotion Access (by location) */}
          <div className="ddf-an-row-2" data-testid="an-promo-access">
            <div className="ddf-an-metric" data-testid="metric-access-departures">
              <div className="ddf-an-metric-head">
                <Eye size={16} className="ddf-an-metric-icon" />
                <span className="ddf-an-metric-label">Departures Promotion Access</span>
              </div>
              <div className="ddf-an-metric-value">{data.promoAccess?.departures ?? 0}</div>
              <div className="ddf-an-metric-sub"><span>Promo card clicks (Departures)</span></div>
            </div>
            <div className="ddf-an-metric" data-testid="metric-access-arrivals">
              <div className="ddf-an-metric-head">
                <Eye size={16} className="ddf-an-metric-icon" />
                <span className="ddf-an-metric-label">Arrivals Promotion Access</span>
              </div>
              <div className="ddf-an-metric-value">{data.promoAccess?.arrivals ?? 0}</div>
              <div className="ddf-an-metric-sub"><span>Promo card clicks (Arrivals)</span></div>
            </div>
          </div>

          {/* Promo Card Clicks — segregated by promo location */}
          <div className="ddf-an-row-2">
            <RankedBarChart
              title="Departures Promo Clicks (ranked)"
              testid="an-bar-chart-departures"
              rows={withTerminal(data.promoClicksByLocation?.departures)}
              color="#0ea5e9"
              emptyText="No Departures promo clicks in this period."
            />
            <RankedBarChart
              title="Arrivals Promo Clicks (ranked)"
              testid="an-bar-chart-arrivals"
              rows={withTerminal(data.promoClicksByLocation?.arrivals)}
              color="#e11d48"
              emptyText="No Arrivals promo clicks in this period."
            />
          </div>

          {/* Traffic Sources + all promo clicks */}
          <div className="ddf-an-row-2">
            <section className="ddf-an-chart-card" data-testid="an-pie-chart">
              <h4 className="ddf-an-chart-title">Traffic Sources</h4>
              <div className="ddf-an-chart-body">
                {pieData.length === 0 ? (
                  <div className="ddf-an-empty">No sessions in this period.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                        {pieData.map((entry) => (
                          <Cell key={entry.name} fill={SOURCE_COLORS[entry.name] || "#6b7280"} />
                        ))}
                      </Pie>
                      <Tooltip formatter={pieTooltipFmt} />
                      <Legend wrapperStyle={LEGEND_STYLE} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </section>

            <RankedBarChart
              title="All Promo Card Clicks (ranked)"
              testid="an-bar-chart"
              rows={withTerminal(data.promoClicks)}
              color="#6366f1"
              emptyText="No promo clicks in this period."
            />
          </div>

          {/* Engagement lists */}
          <div className="ddf-an-row-2">
            <section className="ddf-an-list-card" data-testid="an-category-taps">
              <h4 className="ddf-an-chart-title">Category Taps</h4>
              {data.categoryTaps.length === 0 ? (
                <div className="ddf-an-empty">No taps yet.</div>
              ) : (
                <ul className="ddf-an-list">
                  {data.categoryTaps.slice(0, 8).map((c) => (
                    <li key={c.category}>
                      <span>{c.category}</span>
                      <strong>{c.count}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="ddf-an-list-card" data-testid="an-social-clicks">
              <h4 className="ddf-an-chart-title">Social Media Clicks</h4>
              {data.socialClicks.length === 0 ? (
                <div className="ddf-an-empty">No clicks yet.</div>
              ) : (
                <ul className="ddf-an-list">
                  {data.socialClicks.map((s) => (
                    <li key={s.platform}>
                      <span>{s.platform}</span>
                      <strong>{s.count}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* Email Report */}
          <section className="ddf-an-chart-card" data-testid="an-email-report">
            <h4 className="ddf-an-chart-title">
              <Send size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              Email Full Report
            </h4>
            <p className="ddf-an-help">
              Sends the full analytics report (PDF) plus the subscriber list (XLSX) for the
              currently applied date range.
            </p>
            <div className="ddf-an-email-row">
              <Input
                type="email"
                placeholder="recipient@example.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                data-testid="an-email-input"
                className="ddf-an-email-input"
              />
              <Button
                onClick={() => sendReport(appliedStart, appliedEnd)}
                disabled={emailSending || !emailTo}
                data-testid="an-email-send"
              >
                {emailSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {emailSending ? "Sending…" : "Send Report"}
              </Button>
              <Button
                variant="outline"
                onClick={downloadReportFiles}
                data-testid="an-email-download"
              >
                <Download size={14} /> Download PDF &amp; Excel
              </Button>
            </div>
            {emailStatus && (
              <div
                className={`ddf-an-email-status ${emailStatus.ok ? "is-ok" : "is-err"}`}
                data-testid={emailStatus.ok ? "an-email-ok" : "an-email-err"}
              >
                {emailStatus.msg}
                {!emailStatus.ok && (
                  <button
                    type="button"
                    className="ddf-an-link-btn"
                    style={{ marginLeft: 8 }}
                    onClick={downloadReportFiles}
                    data-testid="an-email-err-download"
                  >
                    Download PDF &amp; Excel instead
                  </button>
                )}
              </div>
            )}
          </section>
        </>
      )}

      {/* Subscriber list dialog */}
      <Dialog open={subsOpen} onOpenChange={setSubsOpen}>
        <DialogContent className="ddf-subs-dialog" data-testid="an-subs-dialog">
          <DialogHeader>
            <DialogTitle>Subscriber List</DialogTitle>
            <DialogDescription>
              {data?.currentRange?.label
                ? `Captured in ${data.currentRange.label} (Asia/Dubai)`
                : "All-time"}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            if (subsLoading) {
              return <div className="ddf-an-loading">Loading…</div>;
            }
            if (subsList.length === 0) {
              return <div className="ddf-an-empty">No subscribers in this range.</div>;
            }
            return (
              <div className="ddf-subs-table-wrap">
                <table className="ddf-subs-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Date</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subsList.map((s) => (
                      <tr key={`${s.email}-${s.createdAt}`}>
                        <td>{s.email}</td>
                        <td>{s.date}</td>
                        <td>{s.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
          <div className="ddf-an-toolbar-right" style={{ marginTop: 12 }}>
            <Button size="sm" onClick={() => downloadSubsXlsx(appliedStart, appliedEnd)} data-testid="an-subs-dialog-xlsx">
              <Download size={14} /> Download Excel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
